import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CarouselSlide, DeliverySettings, WholesaleInquiry, EmailTemplate, CustomProjectInquiry, PaymentMethodConfig } from '../types';

export interface SocialLinks {
  facebook: string;
  instagram: string;
  tiktok: string;
  telegram: string;
  linkedin: string;
}

interface SettingsState {
  carouselSlides: CarouselSlide[];
  deliverySettings: DeliverySettings;
  whatsappNumber: string;
  hotlineNumber: string;
  contactEmail: string;
  contactAddress: string;
  promoText: string;
  socialLinks: SocialLinks;
  wholesaleInquiries: WholesaleInquiry[];
  customInquiries: CustomProjectInquiry[];
  emailTemplates: {
    orderConfirmation: EmailTemplate;
    orderConfirmationCOD: EmailTemplate;
    paymentReceived: EmailTemplate;
    orderProcessingCOD: EmailTemplate;
    orderDispatched: EmailTemplate;
  };
  aiBuilderName: string;
  industrialProjectsName: string;
  paymentMethods: PaymentMethodConfig[];

  // Actions
  updateCarouselSlides: (slides: CarouselSlide[]) => void;
  updateDeliverySettings: (settings: Partial<DeliverySettings>) => void;
  updateContactInfo: (whatsapp: string, hotline: string, email: string, address: string, promoText: string) => void;
  updateSocialLinks: (links: Partial<SocialLinks>) => void;
  updateNavbarLinks: (aiBuilderName: string, industrialProjectsName: string) => void;
  addWholesaleInquiry: (inquiry: Omit<WholesaleInquiry, 'id' | 'createdAt' | 'status'>) => void;
  updateWholesaleInquiryStatus: (id: string, status: WholesaleInquiry['status']) => void;
  addCustomInquiry: (inquiry: Omit<CustomProjectInquiry, 'id' | 'createdAt' | 'status'>) => void;
  updateCustomInquiryStatus: (id: string, status: CustomProjectInquiry['status']) => void;
  updateEmailTemplate: (type: 'orderConfirmation' | 'orderConfirmationCOD' | 'paymentReceived' | 'orderProcessingCOD' | 'orderDispatched', template: EmailTemplate) => void;
  updatePaymentMethods: (methods: PaymentMethodConfig[]) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      carouselSlides: [
        {
          id: '1',
          imageUrl: 'https://images.unsplash.com/photo-1608564697071-ddf911d81370?auto=format&fit=crop&w=1000&h=400&q=60',
          title: 'Unleash Your DIY Creativity',
          subtitle: 'Premium Arduino Kits, Sensors & Robot Modules at Unbeatable Prices.',
          linkUrl: '/category/modules',
        },
        {
          id: '2',
          imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1000&h=400&q=60',
          title: 'Advanced IoT Development',
          subtitle: 'ESP32, ESP8266 & WiFi/Bluetooth Modules for smart home automation.',
          linkUrl: '/category/modules',
        },
        {
          id: '3',
          imageUrl: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1000&h=400&q=60',
          title: 'Wholesale Industrial Sensors',
          subtitle: 'High precision temperature, pressure, & proximity sensors for automation.',
          linkUrl: '/wholesale',
        }
      ],
      deliverySettings: {
        baseFee: 480, // Standard delivery fee in LKR
        freeThreshold: 4990, // Free delivery above 4990 LKR
        codBaseFee: 450, // COD delivery fee in LKR
      },
      whatsappNumber: '94710511136', // Sri Lankan format without + for API links
      hotlineNumber: '+94 77 765 4321',
      contactEmail: 'sensorzonetronic@gmail.com',
      contactAddress: 'Matara, Sri Lanka',
      promoText: 'Order now with Bank Transfer.',
      aiBuilderName: '✨ AI Project Builder',
      industrialProjectsName: '⚙️ Industrial Automation & Projects',
      wholesaleInquiries: [],
      customInquiries: [],
      socialLinks: {
        facebook: 'https://web.facebook.com/profile.php?id=61589080424111',
        instagram: 'https://instagram.com',
        tiktok: 'https://tiktok.com',
        telegram: 'https://telegram.org',
        linkedin: 'https://linkedin.com'
      },
      emailTemplates: {
        orderConfirmation: {
          subject: 'Order Confirmation - {{orderId}}',
          body: 'Dear {{customerName}},\n\nThank you for your order! Your order {{orderId}} has been successfully received.\n\nPlease complete your payment by transferring the full amount of LKR {{total}} to our bank account. We will begin preparing your parcel as soon as the payment transaction is received.\n\nBank Account Details (Commercial Bank):\n- Bank Name: Commercial Bank\n- Branch: Colombo Fort\n- Account Number: 8002345678\n- Account Name: SensorZone (Pvt) Ltd\n\nOnce payment is complete, please upload/WhatsApp your transaction receipt to +{{whatsappNumber}} along with your Order ID.\n\nBest regards,\nSensorZone.lk Team'
        },
        orderConfirmationCOD: {
          subject: 'Order Confirmation (COD) - {{orderId}}',
          body: 'Dear {{customerName}},\n\nThank you for your order! Your order {{orderId}} has been successfully received.\n\nWe will deliver your parcel via courier. Please pay the cash amount of LKR {{total}} to the courier when the package is delivered to your address.\n\nDelivery Address:\n{{deliveryAddress}}\n\nIf you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nBest regards,\nSensorZone.lk Team'
        },
        paymentReceived: {
          subject: 'Payment Received & Processing - {{orderId}}',
          body: 'Dear {{customerName}},\n\nThank you for choosing SensorZone.lk! We have received your payment for order {{orderId}}.\n\nWe have started preparing your parcel and will make sure it is delivered to you as quickly as possible.\n\nThank you again for choosing us, we appreciate your business and will ensure you receive the items soon.\n\nBest regards,\nSensorZone.lk Team'
        },
        orderProcessingCOD: {
          subject: 'Order Confirmed & Processing (COD) - {{orderId}}',
          body: 'Dear {{customerName}},\n\nThank you for choosing SensorZone.lk!\n\nYour Cash on Delivery order {{orderId}} has been confirmed and is now being processed. We are preparing your items for delivery.\n\nYou will pay the cash amount of LKR {{total}} to the courier when the package is delivered to your address.\n\nDelivery Address:\n{{deliveryAddress}}\n\nIf you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nBest regards,\nSensorZone.lk Team'
        },
        orderDispatched: {
          subject: 'Your Order {{orderId}} Has Been Dispatched! 🚀',
          body: 'Dear {{customerName}},\n\nGreat news! Your order {{orderId}} has been dispatched and is on its way to you.\n\n📦 *Tracking Details*:\n- Tracking Number: {{trackingNumber}}\n- Delivery Address: {{deliveryAddress}}\n\nYou can expect delivery within 1-3 business days. If you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nThank you for choosing SensorZone.lk!\n\nBest regards,\nSensorZone.lk Team'
        }
      },
      paymentMethods: [
        { id: 'bank', name: 'Bank Transfer / Deposit', description: 'Direct bank deposit or electronic fund transfer required.', enabled: true },
        { id: 'cod', name: 'Cash on Delivery (COD)', description: 'Pay with cash upon package delivery.', enabled: true }
      ],

      updateCarouselSlides: (slides) => set({ carouselSlides: slides }),
      updateDeliverySettings: (settings) =>
        set((state) => ({
          deliverySettings: { ...state.deliverySettings, ...settings }
        })),
      updateContactInfo: (whatsapp, hotline, email, address, promoText) =>
        set({
          whatsappNumber: whatsapp,
          hotlineNumber: hotline,
          contactEmail: email,
          contactAddress: address,
          promoText: promoText
        }),
      updateSocialLinks: (links) => set((state) => ({
        socialLinks: { ...state.socialLinks, ...links }
      })),
      updateNavbarLinks: (aiBuilder, industrial) => set({
        aiBuilderName: aiBuilder,
        industrialProjectsName: industrial
      }),
      addWholesaleInquiry: (inquiry) => set((state) => ({
        wholesaleInquiries: [
          ...state.wholesaleInquiries,
          {
            ...inquiry,
            id: 'WQ-' + Math.floor(1000 + Math.random() * 9000),
            createdAt: new Date().toISOString(),
            status: 'Pending'
          }
        ]
      })),
      updateWholesaleInquiryStatus: (id, status) => set((state) => ({
        wholesaleInquiries: state.wholesaleInquiries.map((w) =>
          w.id === id ? { ...w, status } : w
        )
      })),
      addCustomInquiry: (inquiry) => set((state) => ({
        customInquiries: [
          ...state.customInquiries,
          {
            ...inquiry,
            id: 'CI-' + Math.floor(1000 + Math.random() * 9000),
            createdAt: new Date().toISOString(),
            status: 'Pending'
          }
        ]
      })),
      updateCustomInquiryStatus: (id, status) => set((state) => ({
        customInquiries: state.customInquiries.map((c) =>
          c.id === id ? { ...c, status } : c
        )
      })),
      updateEmailTemplate: (type, template) => set((state) => ({
        emailTemplates: {
          ...state.emailTemplates,
          [type]: template
        }
      })),
      updatePaymentMethods: (methods) => set({ paymentMethods: methods })
    }),
    {
      name: 'sensorzone-settings',
      onRehydrateStorage: () => (state) => {
        if (state) {
          const isHtml = (text: string) => /<[a-z][\s\S]*>/i.test(text || '');
          const DEFAULT_ORDER_CONFIRMATION = {
            subject: 'Order Confirmation - {{orderId}}',
            body: 'Dear {{customerName}},\n\nThank you for your order! Your order {{orderId}} has been successfully received.\n\nPlease complete your payment by transferring the full amount of LKR {{total}} to our bank account. We will begin preparing your parcel as soon as the payment transaction is received.\n\nBank Account Details (Commercial Bank):\n- Bank Name: Commercial Bank\n- Branch: Colombo Fort\n- Account Number: 8002345678\n- Account Name: SensorZone (Pvt) Ltd\n\nOnce payment is complete, please upload/WhatsApp your transaction receipt to +{{whatsappNumber}} along with your Order ID.\n\nBest regards,\nSensorZone.lk Team'
          };
          const DEFAULT_ORDER_CONFIRMATION_COD = {
            subject: 'Order Confirmation (COD) - {{orderId}}',
            body: 'Dear {{customerName}},\n\nThank you for your order! Your order {{orderId}} has been successfully received.\n\nWe will deliver your parcel via courier. Please pay the cash amount of LKR {{total}} to the courier when the package is delivered to your address.\n\nDelivery Address:\n{{deliveryAddress}}\n\nIf you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nBest regards,\nSensorZone.lk Team'
          };
          const DEFAULT_PAYMENT_RECEIVED = {
            subject: 'Payment Received & Processing - {{orderId}}',
            body: 'Dear {{customerName}},\n\nThank you for choosing SensorZone.lk! We have received your payment for order {{orderId}}.\n\nWe have started preparing your parcel and will make sure it is delivered to you as quickly as possible.\n\nThank you again for choosing us, we appreciate your business and will ensure you receive the items soon.\n\nBest regards,\nSensorZone.lk Team'
          };
          const DEFAULT_ORDER_PROCESSING_COD = {
            subject: 'Order Confirmed & Processing (COD) - {{orderId}}',
            body: 'Dear {{customerName}},\n\nThank you for choosing SensorZone.lk!\n\nYour Cash on Delivery order {{orderId}} has been confirmed and is now being processed. We are preparing your items for delivery.\n\nYou will pay the cash amount of LKR {{total}} to the courier when the package is delivered to your address.\n\nDelivery Address:\n{{deliveryAddress}}\n\nIf you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nBest regards,\nSensorZone.lk Team'
          };
          const DEFAULT_ORDER_DISPATCHED = {
            subject: 'Your Order {{orderId}} Has Been Dispatched! 🚀',
            body: 'Dear {{customerName}},\n\nGreat news! Your order {{orderId}} has been dispatched and is on its way to you.\n\n📦 *Tracking Details*:\n- Tracking Number: {{trackingNumber}}\n- Delivery Address: {{deliveryAddress}}\n\nYou can expect delivery within 1-3 business days. If you have any questions, feel free to reply to this email or message us on WhatsApp (+{{whatsappNumber}}).\n\nThank you for choosing SensorZone.lk!\n\nBest regards,\nSensorZone.lk Team'
          };

          let changed = false;
          const currentTemplates = state.emailTemplates ? { ...state.emailTemplates } : {
            orderConfirmation: DEFAULT_ORDER_CONFIRMATION,
            orderConfirmationCOD: DEFAULT_ORDER_CONFIRMATION_COD,
            paymentReceived: DEFAULT_PAYMENT_RECEIVED,
            orderProcessingCOD: DEFAULT_ORDER_PROCESSING_COD,
            orderDispatched: DEFAULT_ORDER_DISPATCHED
          };

          if (isHtml(currentTemplates.orderConfirmation?.body) || currentTemplates.orderConfirmation?.body?.includes('50%')) {
            currentTemplates.orderConfirmation = DEFAULT_ORDER_CONFIRMATION;
            changed = true;
          }
          if (!currentTemplates.orderConfirmationCOD || isHtml(currentTemplates.orderConfirmationCOD?.body)) {
            currentTemplates.orderConfirmationCOD = DEFAULT_ORDER_CONFIRMATION_COD;
            changed = true;
          }
          if (isHtml(currentTemplates.paymentReceived?.body)) {
            currentTemplates.paymentReceived = DEFAULT_PAYMENT_RECEIVED;
            changed = true;
          }
          if (!currentTemplates.orderProcessingCOD || isHtml(currentTemplates.orderProcessingCOD?.body)) {
            currentTemplates.orderProcessingCOD = DEFAULT_ORDER_PROCESSING_COD;
            changed = true;
          }
          if (!currentTemplates.orderDispatched || isHtml(currentTemplates.orderDispatched?.body)) {
            currentTemplates.orderDispatched = DEFAULT_ORDER_DISPATCHED;
            changed = true;
          }

          if (changed) {
            state.emailTemplates = currentTemplates;
          }

          if (state.deliverySettings && state.deliverySettings.codBaseFee === undefined) {
            state.deliverySettings.codBaseFee = 450;
          }

          if (!state.paymentMethods || state.paymentMethods.length === 0) {
            state.paymentMethods = [
              { id: 'bank', name: 'Bank Transfer / Deposit', description: 'Direct bank deposit or electronic fund transfer required.', enabled: true },
              { id: 'cod', name: 'Cash on Delivery (COD)', description: 'Pay with cash upon package delivery.', enabled: true }
            ];
          }
        }
      }
    }
  )
);
